import type { ReactNode } from 'react'
import { useState } from 'react'

import { faHourglassHalf } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { styled, css } from 'styled-components'

import { getBuyingFormatSequence } from '@b-stock/fe-core/bstock-next/helpers'
import type { OrchestratedAuctionListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import {
  Countdown,
  FormattedMessage,
  Tooltip,
  ClientTimeZoneDateTime,
  DateTimeFormatType,
  parseOptionalISODate,
} from '@b-stock/fe-core/bstock-react'
import { Colors, Shadows } from '@b-stock/fe-core/design-system'

import { useBidFormContext } from '@/buyer-portal/components/features/listing/actions/auction/BidFormProvider'
import { AuctionState } from '@/buyer-portal/components/features/listing/actions/auction/const'

import { Stat } from './Stat'

const DefaultLink = styled.a`
  color: ${Colors.Semantic.BStock.default.lightBackground};
  text-decoration: underline;
`

const TimerTooltip = styled(Tooltip)<{ $noActiveTimePopover: boolean }>`
  ${Shadows.Popovers}
  padding: 1.5rem;
  pointer-events: initial;
  white-space: pre-line;

  ${(o) =>
    o.$noActiveTimePopover
      ? css`
          background: ${Colors.Semantic.Neutral.black};
          color: ${Colors.Semantic.Neutral.white};
        `
      : css`
          background: ${Colors.Semantic.Neutral.white};
          color: ${Colors.Semantic.Neutral.dark_grey};
        `}
`

const ns = 'Auction.details'

export const TimeLeft = ({
  auction,
  displayAsBuyNow,
  isPopcorn,
  isUnscheduled,
}: {
  auction?: OrchestratedAuctionListingDetails['auction']
  displayAsBuyNow?: boolean
  isPopcorn?: boolean
  isUnscheduled?: boolean
}) => {
  const { details } = useBidFormContext()
  const { auctionState, bidDeadline, isEnded, isWithinBuyNowWindow } = details

  const [ref, setRef] = useState<HTMLDivElement | null>(null)

  if (!auction) return null

  const { buyNow, closed } = auction

  const isCanceled = auctionState === AuctionState.Canceled
  const canceledTime = 'canceledTime' in auction ? auction.canceledTime : null

  const targetDate = parseOptionalISODate(
    isCanceled
      ? canceledTime
      : isWithinBuyNowWindow
        ? buyNow?.endTime
        : bidDeadline
  )

  const activeTimePopover = (
    <FormattedMessage
      id={`${ns}.statsBar.tooltip.${isCanceled ? 'canceled' : 'timer'}`}
      values={{
        endDate: (
          <ClientTimeZoneDateTime
            dateTime={targetDate}
            format={DateTimeFormatType.FULL}
          />
        ),
        popcornBiddingLink: (chunks: ReactNode) => (
          <DefaultLink
            target="_blank"
            rel="noreferrer"
            href="https://bstock.com/faq/buyer-faq/bidding/why-does-b-stock-use-proxy-bidding-and-popcorn-bidding/"
          >
            {chunks}
          </DefaultLink>
        ),
      }}
    />
  )

  const popcornPopover = (
    <FormattedMessage id={`${ns}.statsBar.tooltip.popcornPopover`} />
  )

  // CBIN-shaped data counts as Buy Now Only here on purpose: it behaves as
  // BINO until Concurrent is supported — see buyingFormat.ts.
  const buyNowTimePopover = (
    <FormattedMessage
      id={`${ns}.buyNowActions.tooltip.${getBuyingFormatSequence(auction) === 'BINO' ? 'buyNowOnlyTimePopover' : 'buyNowTimePopover'}`}
    />
  )

  if (!targetDate) return null

  return (
    <>
      <Stat ref={setRef} $warning={isPopcorn && !displayAsBuyNow}>
        {isUnscheduled ? (
          <FormattedMessage id={`${ns}.order.unscheduled`} />
        ) : isEnded || closed ? (
          <FormattedMessage
            id={`${ns}.order.${isCanceled ? 'canceledAt' : 'endedAt'}`}
            values={{
              endedAt: (
                <ClientTimeZoneDateTime
                  dateTime={targetDate}
                  format={DateTimeFormatType.SHORT}
                />
              ),
            }}
          />
        ) : auctionState === AuctionState.Ending ? (
          <FormattedMessage id={`${ns}.statsBar.checkingForFinalBids`} />
        ) : (
          <>
            <FontAwesomeIcon icon={faHourglassHalf} />
            <Countdown target={targetDate} />
          </>
        )}
      </Stat>

      {ref && (
        <TimerTooltip
          referenceElement={ref}
          containerElement={ref}
          $noActiveTimePopover={isWithinBuyNowWindow || !!isPopcorn}
        >
          {isWithinBuyNowWindow
            ? buyNowTimePopover
            : isPopcorn
              ? popcornPopover
              : activeTimePopover}
        </TimerTooltip>
      )}
    </>
  )
}
